/**
 * W65 — SalesPlanTieIn
 * Shows the primary owner's quota for the quarter that est_close_date falls in.
 * Reads from sales_plans by user_id + period. Shows progress vs. target.
 */
import { useEffect, useState } from "react";
import { getSession } from "@/lib/session";

interface SalesPlanData {
  annual_quota?: number | null;
  closed_ytd?: number | null;
  weighted_pipeline?: number | null;
  period?: string | null;
}

interface SalesPlanTieInProps {
  ownerUserId?: string | null;
  ownerName?: string | null;
  estCloseDate?: string | null;
}

function quarterFromDate(dateStr: string): { quarter: string; start: string; end: string } {
  const d = new Date(dateStr + "T00:00:00");
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed
  const q = Math.floor(month / 3) + 1;
  const qStart = new Date(year, (q - 1) * 3, 1);
  const qEnd = new Date(year, q * 3, 0);
  return {
    quarter: `Q${q} ${year}`,
    start: qStart.toISOString().slice(0, 10),
    end: qEnd.toISOString().slice(0, 10),
  };
}

function fmtCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

export function SalesPlanTieIn({ ownerUserId, ownerName, estCloseDate }: SalesPlanTieInProps) {
  const [plan, setPlan] = useState<SalesPlanData | null>(null);
  const [loading, setLoading] = useState(false);

  const session = getSession();
  const token = session ? (session as any).access_token ?? (session as any).token ?? "" : "";
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (!ownerUserId) return;
    setLoading(true);
    fetch(`/api/sales-plans?user_id=${ownerUserId}`, { headers: authHeader })
      .then((r) => r.json())
      .then((j) => {
        const plans = j?.sales_plans ?? j?.plans ?? j?.data ?? [];
        if (plans.length > 0) setPlan(plans[0]);
        else setPlan({});
      })
      .catch(() => setPlan({}))
      .finally(() => setLoading(false));
  }, [ownerUserId]);

  if (!ownerUserId || !ownerName) return null;

  const quarterInfo = estCloseDate ? quarterFromDate(estCloseDate) : null;
  const annualQuota = Number(plan?.annual_quota ?? 0);
  const quarterTarget = annualQuota > 0 ? annualQuota / 4 : null;
  const closedYtd = Number(plan?.closed_ytd ?? 0);
  const progressPct = quarterTarget ? Math.min(100, (closedYtd / quarterTarget) * 100) : null;

  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 8,
        border: "1px solid #E5E5E7",
        padding: "12px 16px",
      }}
    >
      <h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: "#0B0B0F" }}>
        Sales Plan Tie-in
      </h3>

      {loading && (
        <div style={{ fontSize: 12, color: "#8E8E94" }}>Loading plan data…</div>
      )}

      {!loading && plan !== null && (
        <>
          {quarterInfo && quarterTarget ? (
            <div style={{ fontSize: 13, color: "#2A2A2E", lineHeight: 1.6 }}>
              Counts toward{" "}
              <strong>{ownerName}</strong>'s{" "}
              <strong>{quarterInfo.quarter}</strong> target —{" "}
              <strong>{fmtCurrency(closedYtd)}</strong> /{" "}
              <strong>{fmtCurrency(quarterTarget)}</strong>
              {progressPct !== null && (
                <span style={{ fontSize: 11, color: "#6B7280", marginLeft: 6 }}>
                  ({Math.round(progressPct)}% attained)
                </span>
              )}
            </div>
          ) : quarterInfo ? (
            <div style={{ fontSize: 13, color: "#2A2A2E" }}>
              Counts toward <strong>{ownerName}</strong>'s <strong>{quarterInfo.quarter}</strong> target.{" "}
              <span style={{ color: "#8E8E94" }}>No quota data on file.</span>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "#8E8E94" }}>
              Set an estimated close date to see quarter alignment.
            </div>
          )}

          {quarterTarget && progressPct !== null && (
            <div style={{ marginTop: 8 }}>
              <div style={{ height: 6, background: "#EFEFEE", borderRadius: 3, overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${progressPct}%`,
                    background: progressPct >= 100 ? "#16A34A" : progressPct >= 70 ? "#EAB308" : "#EA580C",
                    borderRadius: 3,
                    transition: "width 0.3s",
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}

      {!loading && plan === null && (
        <div style={{ fontSize: 12, color: "#8E8E94" }}>No sales plan data available.</div>
      )}
    </div>
  );
}
